import React, { useEffect, useRef, useState } from "react";
import { Link, useHref, useSearchParams } from "react-router-dom";
import { confirmBioMessage } from "@/services/bioLeadService";
import { updateUserDetailsToStorage } from "@/helpers/authHelpers";
import { loginOrSignUp } from "@/helpers/message";
import { getUserId } from "@/helpers/common";
import { pageURLs } from "@/constants";
import "./MessageConfirm.scss";

const OPEN_DELAY_MS = 1400;
const CACHE_PREFIX = "messageConfirm:";

const readCached = (token) => {
  try {
    return JSON.parse(sessionStorage.getItem(`${CACHE_PREFIX}${token}`) || "null");
  } catch {
    return null;
  }
};

const writeCached = (token, response) => {
  try {
    sessionStorage.setItem(`${CACHE_PREFIX}${token}`, JSON.stringify({ result: { ...response?.result, session: undefined } }));
  } catch {}
};

const outcomeOf = (response) => {
  const result = response?.result || {};
  if (result.status === "delivered") return result.signIn ? "signedIn" : "sent";
  return result.status || "failed";
};

const MessageConfirm = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const started = useRef(false);
  const messagesHref = useHref(pageURLs.directMessages);
  const [state, setState] = useState(token ? "working" : "expired");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    const finish = (response, fromCache = false) => {
      const data = response?.result || {};
      setResult(data);
      const outcome = outcomeOf(response);
      if (!fromCache) writeCached(token, response);
      if (outcome === "signedIn") {
        if (data.session) {
          updateUserDetailsToStorage({ result: data.session }, "bio-message");
          Promise.resolve(loginOrSignUp(data.session.user?.Email)).catch(() => {});
        }
        window.setTimeout(() => {
          window.location.assign(`${messagesHref}?id=${data.agent?.userId}`);
        }, OPEN_DELAY_MS);
      }
      setState(outcome);
    };
    const cached = readCached(token);
    if (cached) {
      finish(cached, true);
      return;
    }
    confirmBioMessage(token)
      .then(finish)
      .catch((error) => finish(error?.response?.data));
  }, [token]);

  const agentName = result?.agent?.firstName || result?.agentName || "the agent";
  const bioUrl = result?.agent?.slug ? `/agent-info/${result.agent.slug}` : "/agents";
  const signedIn = Boolean(getUserId());

  const views = {
    working: { title: "Sending your message", body: "One moment." },
    signedIn: {
      title: `Sent to ${agentName}`,
      body: `${agentName} has your message. Opening your conversation, where you'll see the reply.`,
    },
    sent: {
      title: `Sent to ${agentName}`,
      body: `${agentName} has your message. Sign in with this email to read the reply.`,
      action: { label: "Sign in", href: pageURLs.home },
    },
    expired: {
      title: "This link has expired",
      body: "Links work once and for 24 hours. If you already confirmed, your message was sent.",
      action: signedIn ? { label: "Go to your messages", href: pageURLs.directMessages } : { label: "Find the agent again", href: bioUrl },
    },
    not_found: {
      title: `${agentName} isn't taking messages right now`,
      body: "Your message was not sent. You can find another agent in the agent directory.",
      action: { label: "Open the agent directory", href: "/agents" },
    },
    failed: {
      title: "We couldn't send your message",
      body: "Please open the link from your email again in a minute.",
    },
  };
  const view = views[state] || views.failed;

  return (
    <main className="message-confirm">
      <section className="message-confirm__card" aria-live="polite">
        {state === "working" ? <span className="message-confirm__spinner" aria-hidden="true" /> : null}
        <h1>{view.title}</h1>
        <p>{view.body}</p>
        {view.action ? (
          <Link className="btn btn-primary" to={view.action.href}>
            {view.action.label}
          </Link>
        ) : null}
      </section>
    </main>
  );
};

export default MessageConfirm;
